/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { TypographyProps } from '@coursera/cds-core';
import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import { isExternalLink } from 'bundles/common/utils/urlUtils';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/degree-description';

export type PathwayBlockProductCardProps = {
  title?: string | null;
  productUrl?: string | null;
  productType?: string | null;
  courseLength?: number | null;
};

export type PathwayBlockProps = {
  title: React.ReactElement<TypographyProps>;
  productCards?: React.ReactElement[];
};

const styles = {
  blockWrapperDiv: css`
    &:not(:last-of-type) {
      margin-bottom: var(--cds-spacing-300);
    }
    ${breakpoints.down('sm')} {
      &:not(:last-of-type) {
        margin-bottom: var(--cds-spacing-400);
      }
    }
  `,
  link: css`
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  `,
  productCard: css`
    border-radius: 16px;
    padding: var(--cds-spacing-200);
    background-color: var(--cds-color-neutral-background-primary);
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
};

export const PathwayBlockProductCard = ({
  title,
  productUrl,
  productType,
  courseLength,
}: PathwayBlockProductCardProps) => {
  let footerText: string | undefined;

  const { trackClickButton, useGetViewPathwaysCardRef } = useDegreeDescriptionEventingContext();
  const viewRef = useGetViewPathwaysCardRef(title, productUrl);
  const handleOnClick = () => {
    trackClickButton('degree_pathways_card', 'page_body', productUrl, title);
  };

  if (productType && courseLength) {
    footerText = _t('#{productType} · #{courseLengthInHours} hours', {
      productType,
      courseLengthInHours: courseLength,
    });
  } else if (productType && !courseLength) {
    footerText = productType;
  } else if (!productType && courseLength) {
    footerText = _t('#{courseLengthInHours} hours', {
      courseLengthInHours: courseLength,
    });
  }

  return (
    <TrackedDiv
      withVisibilityTracking
      trackingName="pathway_block_product_card"
      data={{ title, productUrl, productType, courseLength }}
    >
      {/* We are correctly setting noopener/noreferrer here, just conditionally */}
      {}
      <a
        href={productUrl !== null ? productUrl : undefined}
        css={styles.link}
        target={isExternalLink(productUrl ?? '') ? '_blank' : undefined}
        rel={isExternalLink(productUrl ?? '') ? 'noopener noreferrer' : undefined}
        onClick={handleOnClick}
        ref={viewRef}
      >
        <div css={styles.productCard}>
          <Typography2 variant="subtitleMedium" component="h5">
            {title}
          </Typography2>
          {footerText && (
            <Typography2 component="p" variant="bodySecondary" color="supportText">
              {footerText}
            </Typography2>
          )}
        </div>
      </a>
    </TrackedDiv>
  );
};

export const PathwayBlock = ({ title, productCards }: PathwayBlockProps) => {
  if (!productCards) return null;
  return (
    <div css={styles.blockWrapperDiv}>
      {title}
      <Grid container spacing={{ xs: 8, sm: 16, md: 24 }}>
        {productCards.map((card) => (
          <Grid key={`pathway-block-${card.key}`} item xs={12} sm={6} md={4}>
            <div>{card}</div>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default PathwayBlock;
